/**
 * Error Handling Utilities for Referral Code Feature
 *
 * Provides centralized error handling, logging, and graceful degradation
 * for the referral code system. Includes structured logging with context
 * and future integration points for monitoring services.
 */

export interface ErrorContext {
  component?: string;
  action?: string;
  token?: string;
  url?: string;
  additionalData?: Record<string, unknown>;
}

/**
 * Log levels for different types of issues
 */
export enum LogLevel {
  INFO = "info",
  WARN = "warn",
  ERROR = "error",
}

/**
 * Centralized logging function with structured context
 *
 * @param level - The log level (info, warn, error)
 * @param message - The main log message
 * @param context - Additional context information
 *
 * TODO: Replace console logging with proper logging provider integration
 * when monitoring infrastructure is available
 */
export function logReferralEvent(
  level: LogLevel,
  message: string,
  context?: ErrorContext,
): void {
  const logData = {
    timestamp: new Date().toISOString(),
    feature: "referral-code",
    message,
    ...context,
  };

  switch (level) {
    case LogLevel.INFO:
      console.log(`[ReferralCode] ${message}`, logData);
      break;
    case LogLevel.WARN:
      console.warn(`[ReferralCode] ${message}`, logData);
      break;
    case LogLevel.ERROR:
      console.error(`[ReferralCode] ${message}`, logData);
      break;
  }
}

/**
 * Handle malformed or invalid referral tokens with appropriate logging
 *
 * @param token - The invalid token
 * @param reason - Reason why the token is invalid
 * @param source - Where the token came from (URL, storage, etc.)
 */
export function handleInvalidToken(
  token: string | null | undefined,
  reason: string,
  source: string,
): void {
  logReferralEvent(LogLevel.WARN, "Invalid referral token encountered", {
    component: "TokenValidation",
    action: "validate",
    token: token || undefined,
    additionalData: { reason, source },
  });
}

/**
 * Apply precedence rules for referral token sources
 *
 * Order of precedence (highest to lowest):
 * 1. URL parameter (always takes precedence)
 * 2. User manual input (overrides stored values)
 * 3. SessionStorage (persistence from previous session)
 *
 * @param sources - Object containing potential token sources
 * @returns The token with highest precedence or null
 */
export interface TokenSources {
  urlToken?: string | null;
  userInput?: string | null;
  storedToken?: string | null;
}

export function applyTokenPrecedence(sources: TokenSources): string | null {
  const { urlToken, userInput, storedToken } = sources;

  // URL parameter has highest precedence
  if (urlToken) {
    logReferralEvent(LogLevel.INFO, "Using URL token (highest precedence)", {
      component: "PrecedenceEngine",
      action: "resolve",
      token: urlToken,
      additionalData: { source: "url" },
    });
    return urlToken;
  }

  // User input overrides stored values
  if (userInput) {
    logReferralEvent(LogLevel.INFO, "Using user input token", {
      component: "PrecedenceEngine",
      action: "resolve",
      token: userInput,
      additionalData: { source: "user_input" },
    });
    return userInput;
  }

  // Stored token as fallback
  if (storedToken) {
    logReferralEvent(LogLevel.INFO, "Using stored token", {
      component: "PrecedenceEngine",
      action: "resolve",
      token: storedToken,
      additionalData: { source: "storage" },
    });
    return storedToken;
  }

  // No token available
  logReferralEvent(
    LogLevel.INFO,
    "No referral token available from any source",
    {
      component: "PrecedenceEngine",
      action: "resolve",
      additionalData: { source: "none" },
    },
  );
  return null;
}

/**
 * Safely execute a function with error handling and logging
 *
 * @param fn - Function to execute
 * @param context - Context for error logging
 * @param fallbackValue - Value to return on error
 * @returns Result of function or fallback value
 */
export function safeExecute<T>(
  fn: () => T,
  context: ErrorContext,
  fallbackValue: T | null = null,
): T | null {
  try {
    return fn();
  } catch (error) {
    logReferralEvent(LogLevel.ERROR, "Safe execution failed", {
      ...context,
      additionalData: {
        error: error instanceof Error ? error.message : "Unknown error",
      },
    });
    return fallbackValue;
  }
}

/**
 * Check system health for referral code functionality
 *
 * @returns Health status object
 */
export interface SystemHealth {
  sessionStorageAvailable: boolean;
  browserSupported: boolean;
  overallHealthy: boolean;
}

export function checkSystemHealth(): SystemHealth {
  const sessionStorageAvailable =
    safeExecute(
      () => {
        if (typeof window === "undefined") return false;
        if (!window.sessionStorage) return false;

        // Test write/read functionality
        const testKey = "__health_check__";
        window.sessionStorage.setItem(testKey, "test");
        const result = window.sessionStorage.getItem(testKey) === "test";
        window.sessionStorage.removeItem(testKey);
        return result;
      },
      { component: "HealthCheck", action: "checkSessionStorage" },
      false,
    ) || false;

  const browserSupported =
    typeof window !== "undefined" && typeof URLSearchParams !== "undefined";

  const overallHealthy = sessionStorageAvailable && browserSupported;

  const health: SystemHealth = {
    sessionStorageAvailable,
    browserSupported,
    overallHealthy,
  };

  logReferralEvent(LogLevel.INFO, "System health check completed", {
    component: "HealthCheck",
    action: "check",
    additionalData: health as any,
  });

  return health;
}
